import { ensurePlusPrefix, removePlusFromBeginning } from 'src/consumer/helpers/utilFunctions'
import {
  buildDateOfBirthFromPatientAge,
  getPatientAgeFieldsFromPatientInfo,
} from './patientAgeUtils'

const DEFAULT_ATTENDANT = {
  name: '',
  primary: true,
  type: 'relative',
}

function trimToEmpty(value) {
  if (typeof value !== 'string') {
    return value ?? ''
  }
  return value.trim()
}

function trimToNull(value) {
  const trimmed = trimToEmpty(value)
  if (typeof trimmed === 'string' && trimmed.length === 0) {
    return null
  }
  return trimmed
}

function sanitizeStringArray(items = []) {
  if (!Array.isArray(items)) {
    return []
  }
  return items
    .map((item) => trimToEmpty(item))
    .filter((item) => typeof item === 'string' && item.length > 0)
}

function sanitizePhoneList(items = []) {
  return sanitizeStringArray(items).map((item) => removePlusFromBeginning(item))
}

export function cleanAttendantsArray(attendantsArray) {
  if (!Array.isArray(attendantsArray)) {
    return []
  }

  return attendantsArray
    .map((item) => ({
      ...item,
      name: trimToEmpty(item?.name),
    }))
    .filter((item) => item?.name)
}

function buildPrimaryPatientProfile(patientDetails = {}) {
  if (patientDetails?.primaryPatientProfile?.patientId) {
    return patientDetails.primaryPatientProfile
  }

  if (
    patientDetails?.dependentRelation &&
    patientDetails?.relatedToExistingPatient?.id &&
    patientDetails?.relatedToExistingPatient?.userId
  ) {
    return {
      apiParam: 'primary-patient-profile',
      patientId: patientDetails.relatedToExistingPatient.id,
      mobile: patientDetails.relatedToExistingPatient.mobile,
      email: patientDetails.relatedToExistingPatient.email,
      userId: patientDetails.relatedToExistingPatient.userId,
      dependentRelation: patientDetails.dependentRelation,
    }
  }

  return null
}

function buildLocationPayload(patientDetails = {}) {
  const existingLocation =
    patientDetails?.location && typeof patientDetails.location === 'object'
      ? patientDetails.location
      : {}

  const address = trimToNull(patientDetails?.address ?? existingLocation?.address)
  const city = trimToNull(patientDetails?.city ?? existingLocation?.city)
  const pincode = trimToNull(patientDetails?.pincode ?? existingLocation?.pincode)
  const state = trimToNull(patientDetails?.state ?? existingLocation?.state)

  const nextLocation = {
    ...existingLocation,
    address,
    city,
    pincode,
    state,
  }

  const hasUsefulLocationValue = Object.values(nextLocation).some(
    (value) => value !== null && value !== undefined && value !== '',
  )

  return hasUsefulLocationValue ? nextLocation : undefined
}

export function normalizePatientInfoForForm(
  patientInfo = {},
  { defaultCommunicationLanguage = 'en', withDefaultAttendant = true } = {},
) {
  const location =
    patientInfo?.location && typeof patientInfo.location === 'object' ? patientInfo.location : {}
  const normalizedAttendants = cleanAttendantsArray(patientInfo?.attendants)
  const normalizedAgeFields = getPatientAgeFieldsFromPatientInfo(patientInfo)

  return {
    ...patientInfo,
    name: patientInfo?.name || '',
    email: patientInfo?.email || '',
    mobile: patientInfo?.mobile ? removePlusFromBeginning(patientInfo.mobile) : '',
    ...normalizedAgeFields,
    note: patientInfo?.note || patientInfo?.notes || '',
    referredBy: patientInfo?.referredBy || '',
    address: patientInfo?.address ?? location?.address ?? '',
    city: patientInfo?.city ?? location?.city ?? '',
    pincode: patientInfo?.pincode ?? location?.pincode ?? '',
    state: patientInfo?.state ?? location?.state ?? '',
    attendants:
      normalizedAttendants.length > 0
        ? normalizedAttendants
        : withDefaultAttendant
          ? [DEFAULT_ATTENDANT]
          : [],
    altMobiles: sanitizePhoneList(patientInfo?.altMobiles),
    altEmails: sanitizeStringArray(patientInfo?.altEmails),
    communicationToAttendant: Boolean(patientInfo?.communicationToAttendant),
    defaultCommunicationLanguage:
      patientInfo?.defaultCommunicationLanguage || defaultCommunicationLanguage || 'en',
    uhId: patientInfo?.uhId || '',
    importData: {
      ...(patientInfo?.importData || {}),
      importedFromId: patientInfo?.importData?.importedFromId || '',
    },
  }
}

export function buildPatientUpsertPayload(
  patientDetails = {},
  {
    clinicId,
    clinicBrandId,
    mentorId,
    patientId,
    includePatientNumber = false,
    anonymous = false,
  } = {},
) {
  const mobile = trimToNull(patientDetails?.mobile)
  const payload = {
    apiParam: 'PatientInfo',
    id: patientId || patientDetails?.id || undefined,
    clinicId: clinicId ?? patientDetails?.clinicId,
    clinicBrandId: clinicBrandId ?? patientDetails?.clinicBrandId,
    mentorId: mentorId ?? patientDetails?.mentorId,
    name: trimToEmpty(patientDetails?.name),
    email: trimToNull(patientDetails?.email),
    mobile: mobile ? ensurePlusPrefix(removePlusFromBeginning(mobile)) : null,
    dateOfBirth: buildDateOfBirthFromPatientAge(patientDetails),
    gender: patientDetails?.gender || null,
    note: trimToNull(patientDetails?.note),
    referredBy: trimToNull(patientDetails?.referredBy),
    attendants: cleanAttendantsArray(patientDetails?.attendants),
    altMobiles: sanitizePhoneList(patientDetails?.altMobiles).map(ensurePlusPrefix),
    altEmails: sanitizeStringArray(patientDetails?.altEmails),
    communicationToAttendant: Boolean(patientDetails?.communicationToAttendant),
    defaultCommunicationLanguage: patientDetails?.defaultCommunicationLanguage || undefined,
    communicationToPref: patientDetails?.communicationToPref || undefined,
    uhId: trimToNull(patientDetails?.uhId),
    importData: patientDetails?.importData || undefined,
    anonymous:
      Boolean(anonymous) ||
      Boolean(patientDetails?.anonymous) ||
      Boolean(patientDetails?.isAnonymous) ||
      Boolean(patientDetails?.anonymousPatient),
    primaryPatientProfile: buildPrimaryPatientProfile(patientDetails) || undefined,
    location: buildLocationPayload(patientDetails),
  }

  if (includePatientNumber && patientDetails?.patientNumber) {
    payload.patientNumber = patientDetails.patientNumber
  }

  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined),
  )
}
